import { z } from "zod/v4";
import { ITEM_SUB_GROUP_LABELS, SUB_GROUP_SECTION, type ItemSubGroupId } from "./work-type";
import { COST_NAV_HEADING_CATALOG, navHeadingDisplayLabel } from "./cost-navigation-names";
export { COST_NAV_NAMES, COST_NAV_HEADING_CATALOG, NAV_SECTION_HEADING_KEYS, getNavHeadingEntry, getNavHeadingKey, getNavSectionDisplayLabel, getNavItemDisplayLabel, navHeadingDisplayLabel, type NavHeadingEntry } from "./cost-navigation-names";

export const HEADING_SECTIONS = ["Preparation", "Demolition", "Substructure", "Structure", "Roofing", "External Works", "Finishes", "Services", "Other"] as const;
const parents: Record<string, string> = {
  demolition_works: "Demolition", ground_works: "Substructure",
  masonry_works: "Structure", structural_steel: "Structure", thermal_insulation: "Structure",
  pitched_roofing: "Roofing", flat_roofing: "Roofing", roof_windows: "Roofing", fascias_guttering: "Roofing",
  windows_doors: "External Works", first_fix_electrics: "Services", second_fix_electrics: "Services",
  first_fix_plumbing: "Services", second_fix_plumbing: "Services", heating: "Services",
  drylining_works: "Finishes", second_fix_joinery: "Finishes", making_good: "Finishes", decorative_finishes: "Finishes",
};
export const QUOTE_HEADING_CATALOG = [
  ...HEADING_SECTIONS.map(label => ({ key: `section:${label}`, label, group: "Sections" })),
  ...Object.entries(ITEM_SUB_GROUP_LABELS).map(([id, label]) => ({ key: `subgroup:${id}`, label, group: parents[id] })),
];
export type QuoteHeadingNames = Record<string, string>;

export const quoteHeadingNamesSchema = z.record(z.string(), z.string().trim().max(60))
  .superRefine((names, ctx) => {
    const keys = new Set([...QUOTE_HEADING_CATALOG, ...COST_NAV_HEADING_CATALOG].map(h => h.key));
    for (const [key, value] of Object.entries(names)) {
      if (!keys.has(key) || /[<>\u0000-\u001f]/.test(value)) {
        ctx.addIssue({ code: "custom", path: [key], message: "Use a supported heading and plain text only." });
      }
    }
    const seen = new Set<string>();
    for (const heading of QUOTE_HEADING_CATALOG) {
      const label = names[heading.key] || heading.label;
      const identity = `${heading.group}:${label.toLocaleLowerCase()}`;
      if (seen.has(identity)) ctx.addIssue({ code: "custom", path: [heading.key], message: `Names in ${heading.group} must be different.` });
      seen.add(identity);
    }
    const navSeen = new Set<string>();
    for (const entry of COST_NAV_HEADING_CATALOG) {
      const identity = `${entry.group}:${navHeadingDisplayLabel(entry, names).toLocaleLowerCase()}`;
      if (navSeen.has(identity)) ctx.addIssue({ code: "custom", path: [entry.key], message: "Category names in the same menu must be different." });
      navSeen.add(identity);
    }
    // Summary mode combines several detailed stages under Structure.
    // These subgroups become siblings there, even if separate in detailed mode.
    const summarySeen = new Set<string>();
    for (const [id, defaultName] of Object.entries(ITEM_SUB_GROUP_LABELS)) {
      const key = `subgroup:${id}`;
      const group = SUB_GROUP_SECTION[id as ItemSubGroupId];
      const identity = `${group}:${(names[key] || defaultName).toLocaleLowerCase()}`;
      if (summarySeen.has(identity)) ctx.addIssue({ code: "custom", path: [key], message: `Names in the ${group} summary must be different.` });
      summarySeen.add(identity);
    }
  }).transform(names => Object.fromEntries(Object.entries(names).filter(([key, value]) =>
    value && value !== [...QUOTE_HEADING_CATALOG, ...COST_NAV_HEADING_CATALOG].find(h => h.key === key)?.label)));

/** Prepare one edit, retaining every other preference and its quote counterpart.
 * Validation uses effective names, including legacy quote-only overrides. */
export function patchNavigationHeadingName(names: QuoteHeadingNames, key: string, value: string): QuoteHeadingNames {
  const entry = COST_NAV_HEADING_CATALOG.find(h => h.key === key);
  if (!entry) throw new Error("Unsupported category or subcategory.");
  const next = { ...names };
  const trimmed = value.trim();
  delete next[key];
  if (entry.quoteKey) delete next[entry.quoteKey];
  if (trimmed && trimmed !== entry.label) {
    next[key] = trimmed;
    if (entry.quoteKey) next[entry.quoteKey] = trimmed;
  }
  return quoteHeadingNamesSchema.parse(next);
}

/** Only resolve built-in display headings. Never use the returned text as an identity. */
export function quoteHeadingLabel(names: QuoteHeadingNames | null | undefined, label: string, kind: "section" | "subgroup" = "section"): string {
  const heading = QUOTE_HEADING_CATALOG.find(h => h.key.startsWith(`${kind}:`) && (h.label === label || h.key === `${kind}:${label}`));
  return (heading && names?.[heading.key]) || heading?.label || label;
}
